import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Crown, Spinner, ArrowSquareOut, CheckCircle, Warning } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Subscription management card on the Profile page.
 *
 * 3 mutually exclusive states:
 *   1. No subscription yet → show 3 plans (sub_1m / sub_3m / sub_6m) with
 *      "Abonnieren" buttons that hit /payments/create-subscription.
 *   2. Active subscription → show plan, next renewal date, and a single
 *      "Abonnement verwalten" button that opens the Stripe Customer Portal.
 *   3. Subscription set to cancel at period_end → same as 2 + amber notice.
 *
 * One-off payment flow remains untouched elsewhere on the profile page —
 * users who want to pay one month manually still can.
 */
const PLANS = [
  { id: 'sub_1m', label: '1 Monat',  price: 100, perMonth: 100, savings: null },
  { id: 'sub_3m', label: '3 Monate', price: 80,  perMonth: 80,  savings: '20% günstiger', highlight: true },
  { id: 'sub_6m', label: '6 Monate', price: 60,  perMonth: 60,  savings: '40% günstiger' },
];

const PACKAGE_LABEL = {
  sub_1m: 'Habla 1 mes',
  sub_3m: 'Habla 3 meses',
  sub_6m: 'Habla 6 meses',
};

const SubscriptionCard = () => {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/payments/subscription`, { withCredentials: true });
      setSub(res.data);
    } catch {
      setSub(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const subscribe = async (packageId) => {
    setBusyAction(packageId);
    try {
      const res = await axios.post(
        `${API}/payments/create-subscription`,
        { package_id: packageId, origin_url: window.location.origin },
        { withCredentials: true },
      );
      try {
        const price = { sub_1m: 100, sub_3m: 80, sub_6m: 60 }[packageId] || 0;
        const { trackEvent } = await import('../utils/metaPixel');
        trackEvent('InitiateCheckout', {
          content_ids: [packageId],
          content_type: 'subscription',
          content_name: { sub_1m: '1 Monat', sub_3m: '3 Monate', sub_6m: '6 Monate' }[packageId],
          currency: 'EUR',
          value: price,
          num_items: 1,
        });
        await new Promise(r => setTimeout(r, 250));
      } catch { /* pixel disabled */ }
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Starten des Abos');
      setBusyAction(null);
    }
  };

  const openPortal = async () => {
    setBusyAction('portal');
    try {
      const res = await axios.post(
        `${API}/payments/portal`,
        { origin_url: window.location.origin },
        { withCredentials: true },
      );
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Portal nicht verfügbar');
      setBusyAction(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6">
        <div className="flex items-center gap-2 text-sm text-[#78716C]">
          <Spinner size={14} className="animate-spin" /> Lade Abo-Status…
        </div>
      </div>
    );
  }

  const hasActiveSub = sub?.subscription_status === 'active' || sub?.subscription_status === 'trialing';
  const willCancel = sub?.subscription_cancel_at_period_end;
  const renewDate = sub?.premium_until ? new Date(sub.premium_until) : null;
  const renewStr = renewDate ? renewDate.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' }) : null;

  // ── State 2 + 3: active subscription ───────────────────────────────────
  if (hasActiveSub) {
    return (
      <div
        className="bg-white rounded-2xl border border-[#D6D3D1] p-6"
        data-testid="subscription-card-active"
      >
        <div className="flex items-center gap-2 mb-4">
          <Crown size={22} className="text-amber-500" weight="fill" />
          <h3 className="font-heading text-lg font-bold text-[#292524]">Dein Abonnement</h3>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-wider font-bold text-amber-700 mb-1">Plan</p>
              <p className="text-lg font-bold text-[#292524]">{PACKAGE_LABEL[sub?.subscription_package_id] || 'Habla Premium'}</p>
            </div>
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">
              <CheckCircle size={14} weight="fill" /> Aktiv
            </span>
          </div>
        </div>

        <div className="space-y-2 text-sm mb-4">
          {willCancel ? (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-start gap-2" data-testid="sub-canceling-notice">
              <Warning size={18} className="text-amber-600 flex-shrink-0 mt-0.5" weight="fill" />
              <div>
                <p className="text-[#292524] font-semibold">Abo läuft aus</p>
                <p className="text-[#57534E] text-xs mt-0.5">
                  Du hast bereits gekündigt. Dein Premium-Zugang bleibt
                  bis zum <strong>{renewStr}</strong> aktiv.
                </p>
              </div>
            </div>
          ) : (
            <p className="text-[#57534E]">
              <span className="text-[#A8A29E]">Nächste Verlängerung:</span>{' '}
              <strong className="text-[#292524]">{renewStr}</strong>
            </p>
          )}
        </div>

        <button
          onClick={openPortal}
          disabled={busyAction === 'portal'}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#292524] hover:bg-[#1c1917] disabled:bg-stone-300 text-white py-2.5 rounded-lg font-semibold"
          data-testid="open-portal-btn"
        >
          {busyAction === 'portal' ? (
            <Spinner size={16} className="animate-spin" />
          ) : (
            <ArrowSquareOut size={16} weight="bold" />
          )}
          Abonnement verwalten
        </button>
        <p className="text-[11px] text-[#A8A29E] text-center mt-2">
          Du wirst zu Stripe weitergeleitet, wo du Karte aktualisieren,
          Rechnungen herunterladen und kündigen kannst.
        </p>
      </div>
    );
  }

  // ── State 1: no subscription yet ───────────────────────────────────────
  return (
    <div
      className="bg-white rounded-2xl border border-[#D6D3D1] p-6"
      data-testid="subscription-card-plans"
    >
      <div className="flex items-center gap-2 mb-1">
        <CreditCard size={22} className="text-[#AB1C24]" weight="fill" />
        <h3 className="font-heading text-lg font-bold text-[#292524]">Abonnement starten</h3>
      </div>
      <p className="text-sm text-[#78716C] mb-4">
        Wähle dein Abo — wird automatisch monatlich verlängert,
        kündbar jederzeit aus dem Profil.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {PLANS.map(p => (
          <div
            key={p.id}
            className={`rounded-xl border-2 p-4 flex flex-col ${
              p.highlight
                ? 'border-[#AB1C24] bg-gradient-to-br from-red-50 to-white relative'
                : 'border-stone-200 bg-[#FAF9F6]'
            }`}
            data-testid={`plan-${p.id}`}
          >
            {p.highlight && (
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold bg-[#AB1C24] text-white px-2 py-0.5 rounded-full uppercase tracking-wider">
                Beliebt
              </span>
            )}
            <p className="font-heading font-bold text-[#292524] text-base">{p.label}</p>
            <p className="mt-1 text-2xl font-bold text-[#AB1C24]">
              {p.perMonth}€<span className="text-sm text-[#78716C] font-normal">/Monat</span>
            </p>
            {p.savings && (
              <p className="text-[11px] text-emerald-700 font-semibold mt-0.5">{p.savings}</p>
            )}
            <button
              onClick={() => subscribe(p.id)}
              disabled={busyAction === p.id}
              className={`mt-3 w-full py-2 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-1 ${
                p.highlight
                  ? 'bg-[#AB1C24] hover:bg-[#8A151C] text-white'
                  : 'bg-stone-800 hover:bg-stone-900 text-white'
              } disabled:bg-stone-300`}
              data-testid={`subscribe-${p.id}-btn`}
            >
              {busyAction === p.id ? <Spinner size={14} className="animate-spin" /> : 'Abonnieren'}
            </button>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-[#A8A29E] text-center mt-3">
        Sichere Zahlung über Stripe · jederzeit kündbar · keine versteckten Kosten
      </p>
    </div>
  );
};

export default SubscriptionCard;
